import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";
import { TrendingUp, TrendingDown, Minus, ChevronDown } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Legend,
} from "recharts";

function fmt2(n) { return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmt1(n) { return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }); }

const ACCENT_BY_DEPT = {
  MOLD:       "#EC4899",
  FRAME:      "#F59E0B",
  GFRC:       "#00D4FF",
  FINISH:     "#A78BFA",
  FIBERGLASS: "#34D399",
};

const PROD_COLOR   = "#EC4899";   // Actual SF/Hr — pink (matches Efficiency Trend)
const COST_COLOR   = "#F59E0B";   // Actual $/SF — amber
const BUD_PROD_C   = "#22D3EE";   // Budget SF/Hr reference — cyan dashed
const BUD_COST_C   = "#FDE68A";   // Budget $/SF reference — pale amber dashed

function DeltaChip({ pct, positiveGood, testId }) {
  if (pct === null || pct === undefined) {
    return <span data-testid={testId} className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground/60">—</span>;
  }
  const isGood = positiveGood ? pct >= 0 : pct <= 0;
  const color  = pct === 0 ? "#94A3B8" : (isGood ? "#34D399" : "#F87171");
  const Icon   = pct === 0 ? Minus : (pct > 0 ? TrendingUp : TrendingDown);
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 text-xs font-mono font-semibold tabular-nums"
      style={{ color }}
    >
      <Icon size={11} />
      {pct > 0 ? "+" : ""}{fmt1(pct)}%
    </span>
  );
}

function TooltipBody({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="bg-[#0A0B10] border border-border/80 px-3 py-2 shadow-lg text-xs font-mono">
      <div className="text-white/90 font-semibold mb-1">{label}</div>
      <div style={{ color: PROD_COLOR }}>SF/Hr: {fmt2(row.actual_sf_per_hour)}</div>
      <div style={{ color: COST_COLOR }}>$/SF: ${fmt2(row.actual_cost_per_sf)}</div>
      <div className="text-muted-foreground mt-1 pt-1 border-t border-border/40">
        {fmt1(row.sf)} sf · {fmt2(row.hours)} h · ${fmt2(row.cost)}
      </div>
    </div>
  );
}

export default function JobBudgetTrendCard({ dept = "MOLD", showMexico = false }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [selectedJob, setSelectedJob] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [budgetedOnly, setBudgetedOnly] = useState(true);
  const [loading, setLoading] = useState(true);

  const accent = ACCENT_BY_DEPT[dept] || "#00D4FF";

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (selectedJob) p.job = selectedJob;
    if (selectedCategory) p.category = selectedCategory;
    return p;
  }, [filter, showMexico, selectedJob, selectedCategory]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/departments/${dept}/job-budget-trend`, { params })
      .then((r) => {
        if (cancelled) return;
        setJobs(r.data.jobs || []);
        setCategories(r.data.categories || []);
        setSeries(r.data.series || []);
        setSelected(r.data.selected || null);
        // Auto-pick the first budgeted job on initial load so the chart is
        // populated the moment the user reaches the section. If a category
        // filter was just applied and the current job doesn't match, switch
        // to the first job in the filtered set.
        const list = r.data.jobs || [];
        const stillInList = selectedJob && list.some((j) => j.job === selectedJob);
        if (!stillInList && list.length) {
          const first = list.find((j) => j.has_budget) || list[0];
          if (first) setSelectedJob(first.job);
        }
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params, dept]);

  const jobsForDropdown = useMemo(() => {
    return jobs.filter((j) => (budgetedOnly ? j.has_budget : true));
  }, [jobs, budgetedOnly]);

  return (
    <section
      className="relative bg-[#12141D] border border-border overflow-hidden p-5"
      data-testid={`job-budget-trend-card-${dept.toLowerCase()}`}
    >
      <span
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${accent} 0%, #FBBF24 100%)` }}
      />

      <div className="flex flex-wrap items-start justify-between gap-4 mb-1">
        <div>
          <h3 className="font-heading font-bold text-xl">Productivity & Unit Material Trend</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Daily SF/Hr and $/SF for the selected job — actuals plotted vs Budget reference lines
          </p>
        </div>
        <div className="text-[10px] tracking-widest uppercase text-muted-foreground text-right">
          {series.length} days
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <DateFilter value={filter} onChange={setFilter} />

        {/* Job selector — the heart of this card */}
        <div className="relative" data-testid={`job-picker-${dept.toLowerCase()}`}>
          <select
            value={selectedJob}
            onChange={(e) => setSelectedJob(e.target.value)}
            className="appearance-none bg-[#0A0B10] border border-border pl-3 pr-9 py-2 text-sm font-mono focus:outline-none focus:border-[#00D4FF] cursor-pointer min-w-[240px]"
            data-testid={`job-select-${dept.toLowerCase()}`}
          >
            <option value="">Select job…</option>
            {jobsForDropdown.map((j) => (
              <option key={j.job} value={j.job}>
                {j.job}{j.has_budget ? "" : "  (no budget)"}
              </option>
            ))}
          </select>
          <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>

        <label
          className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase text-muted-foreground cursor-pointer select-none"
          data-testid={`budgeted-only-toggle-${dept.toLowerCase()}`}
        >
          <input
            type="checkbox"
            checked={budgetedOnly}
            onChange={(e) => setBudgetedOnly(e.target.checked)}
            className="w-3.5 h-3.5 accent-[#00D4FF]"
          />
          Budgeted jobs only
        </label>

        {/* Category filter — narrows the budget rows (and hence the reference
             lines) to a specific category name like "SETUP MOLD" or "CNC".
             Powered by the distinct-category list returned by the endpoint. */}
        <div className="relative" data-testid={`category-picker-${dept.toLowerCase()}`}>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="appearance-none bg-[#0A0B10] border border-border pl-3 pr-9 py-2 text-[11px] tracking-widest uppercase font-mono focus:outline-none focus:border-[#00D4FF] cursor-pointer min-w-[200px]"
            data-testid={`category-select-${dept.toLowerCase()}`}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
        {selectedCategory && (
          <button
            type="button"
            onClick={() => setSelectedCategory("")}
            className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-[#00D4FF] underline-offset-2 hover:underline"
            data-testid={`clear-category-${dept.toLowerCase()}`}
          >
            Clear
          </button>
        )}
      </div>

      {/* Selected-job stat strip: 4 cards showing Actual vs Budget for each metric */}
      {selected && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid={`selected-stats-${dept.toLowerCase()}`}>
          <StatBlock
            label="Actual SF/Hr"    color={PROD_COLOR}
            value={fmt2(selected.actual_sf_per_hour)}
            sub={selected.budget_sf_per_hour ? `budget ${fmt2(selected.budget_sf_per_hour)}` : "no budget"}
          />
          <StatBlock
            label="Prod Δ vs Budget"
            value={<DeltaChip pct={selected.prod_variance_pct} positiveGood={true} testId={`chart-prod-var-${dept.toLowerCase()}`} />}
          />
          <StatBlock
            label="Actual $/SF"      color={COST_COLOR}
            value={`$${fmt2(selected.actual_cost_per_sf)}`}
            sub={selected.budget_cost_per_sf ? `budget $${fmt2(selected.budget_cost_per_sf)}` : "no budget"}
          />
          <StatBlock
            label="Mat Δ vs Budget"
            value={<DeltaChip pct={selected.mat_variance_pct} positiveGood={false} testId={`chart-mat-var-${dept.toLowerCase()}`} />}
          />
        </div>
      )}

      {/* Chart */}
      {loading && (
        <div className="h-[360px] flex items-center justify-center text-sm text-muted-foreground uppercase tracking-widest">
          Loading…
        </div>
      )}

      {!loading && !selectedJob && (
        <div className="h-[360px] flex items-center justify-center text-sm text-muted-foreground uppercase tracking-widest">
          {showMexico ? "Mexico view — no budget data" : "Select a job to see its trend"}
        </div>
      )}

      {!loading && selectedJob && series.length === 0 && (
        <div className="h-[360px] flex items-center justify-center text-sm text-muted-foreground uppercase tracking-widest">
          No daily activity for {selectedJob} in this date range
        </div>
      )}

      {!loading && selectedJob && series.length > 0 && (
        <div className="h-[380px]" data-testid={`chart-${dept.toLowerCase()}`}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 6, right: 30, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "monospace" }}
                tickLine={{ stroke: "#334155" }}
                axisLine={{ stroke: "#334155" }}
                minTickGap={35}
                tickFormatter={(d) => d?.slice(5)}
              />
              <YAxis
                yAxisId="left"
                domain={[0, "auto"]}
                tick={{ fill: PROD_COLOR, fontSize: 10, fontFamily: "monospace" }}
                tickLine={{ stroke: "#334155" }}
                axisLine={{ stroke: "#334155" }}
                label={{ value: "SF/Hr", angle: -90, position: "insideLeft", fill: PROD_COLOR, fontSize: 10, fontFamily: "monospace" }}
              />
              <YAxis
                yAxisId="right" orientation="right"
                domain={[0, "auto"]}
                tick={{ fill: COST_COLOR, fontSize: 10, fontFamily: "monospace" }}
                tickLine={{ stroke: "#334155" }}
                axisLine={{ stroke: "#334155" }}
                label={{ value: "Cost/SF", angle: 90, position: "insideRight", fill: COST_COLOR, fontSize: 10, fontFamily: "monospace" }}
              />
              <Tooltip content={<TooltipBody />} />
              <Legend
                verticalAlign="bottom"
                wrapperStyle={{ paddingTop: 10, fontSize: 11, fontFamily: "monospace" }}
                iconType="plainline"
              />

              {/* Budget reference lines — horizontal targets the estimator committed to.
                   ifOverflow="extendDomain" guarantees the line renders even
                   when the budget value is outside the auto Y-axis range. */}
              {selected?.budget_sf_per_hour > 0 && (
                <ReferenceLine
                  yAxisId="left"
                  y={selected.budget_sf_per_hour}
                  stroke={BUD_PROD_C}
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  ifOverflow="extendDomain"
                  label={{
                    value: `Budget SF/Hr ${fmt2(selected.budget_sf_per_hour)}`,
                    position: "insideBottomLeft",
                    offset: 8,
                    fill: BUD_PROD_C,
                    fontSize: 10,
                    fontFamily: "monospace",
                  }}
                />
              )}
              {selected?.budget_cost_per_sf > 0 && (
                <ReferenceLine
                  yAxisId="right"
                  y={selected.budget_cost_per_sf}
                  stroke={BUD_COST_C}
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  ifOverflow="extendDomain"
                  label={{
                    value: `Budget $/SF $${fmt2(selected.budget_cost_per_sf)}`,
                    position: "insideBottomRight",
                    offset: 8,
                    fill: BUD_COST_C,
                    fontSize: 10,
                    fontFamily: "monospace",
                  }}
                />
              )}

              <Line
                yAxisId="left"
                type="monotone"
                dataKey="actual_sf_per_hour"
                name="Actual SF/Hr"
                stroke={PROD_COLOR}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="actual_cost_per_sf"
                name="Actual $/SF"
                stroke={COST_COLOR}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

function StatBlock({ label, value, sub, color }) {
  return (
    <div className="bg-[#0A0B10] border border-border/60 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono font-semibold text-lg tabular-nums mt-0.5" style={{ color: color || "#E5E7EB" }}>
        {value}
      </div>
      {sub && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
